"use client";

import {
  GuidedTour,
  type GuidedTourStep,
} from "@/components/tutorial/GuidedTour";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/context/LanguageContext";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

const TOUR_HANDOFF_STORAGE_KEY = "softiq:page-tour-handoff";
type TourHandoff = "watchdog" | "portfolio";

type PageTourButtonProps = {
  hiddenTargets?: string[];
  className?: string;
  hasPortfolioEstate?: boolean;
  hasWatchdog?: boolean;
  hasWatchdogResults?: boolean;
};

function buildDashboardSteps(language: "cs" | "en"): GuidedTourStep[] {
  return language === "cs"
    ? [
        {
          target: "[data-tour='dashboard-profile']",
          title: "Začněte profilem",
          text: "Nejdřív si otevřete profil a doplňte jméno i fakturační údaje. Aplikace je pak může použít pro automatické generování faktur a správné údaje v e-mailech.",
        },
        {
          target: "[data-tour='dashboard-profile-billing']",
          title: "Co v profilu vyplnit",
          text: "Jméno se používá v komunikaci. Fakturační údaje slouží pro faktury: název subjektu, IČ a adresa. Bez nich nejde fakturu vytvořit správně automaticky.",
        },
        {
          target: "[data-tour='dashboard-watchdog']",
          title: "Přehled hlídacího psa",
          text: "Tady sledujete uložená hledání nemovitostí. Hlídací pes vám pomůže hlídat nové inzeráty podle lokality, typu nabídky a dalších filtrů.",
        },
        {
          target: "[data-tour='watchdog-offer-type-field']",
          title: "Vytvořte hlídacího psa",
          text: "Tady zvolíte, jestli chcete sledovat prodej nebo pronájem. Bez toho by se míchaly úplně jiné typy inzerátů.",
        },
        {
          target: "[data-tour='watchdog-detailed-search-button']",
          title: "Podrobnější filtry",
          text: "Podrobnější hledání slouží pro cenu, dispozici, plochu nebo další omezení. Pomůže snížit počet nerelevantních nabídek.",
        },
        {
          target: "[data-tour='watchdog-create-form']",
          title: "Co uvidíte potom",
          text: "Po uložení se zde začnou objevovat nalezené inzeráty. Zajímavé si uložíte, slabé skryjete a výsledky můžete porovnávat podle ceny.",
        },
        {
          target: "[data-tour='dashboard-portfolio']",
          title: "Přehled portfolia",
          text: "Portfolio slouží pro nemovitosti, které vlastníte, spravujete nebo užíváte. Na jednom místě pak držíte finance, smlouvy, připomenutí i důležité kontakty.",
        },
        {
          target: "[data-tour='portfolio-name-field']",
          title: "Přidejte nemovitost",
          text: "Název je vaše interní pojmenování nemovitosti. Pomůže vám rychle poznat, se kterou jednotkou pracujete.",
        },
        {
          target: "[data-tour='portfolio-details-button']",
          title: "Doplňte detaily",
          text: "Tlačítko „Doplnit údaje“ otevře pořizovací cenu, aktuální hodnotu, adresu a další údaje. Díky nim dává smysl výnos, cash flow i analytika.",
        },
        {
          target: "[data-tour='portfolio-tenant-button']",
          title: "Nájemce a správce",
          text: "Nájemce pomáhá sledovat platby a komunikaci. Je to kontakt, který se potom hodí i pro připomínky a přehled plateb.",
        },
        {
          target: "[data-tour='dashboard-activity']",
          title: "Aktivita",
          text: "Tady uvidíte importy bankovních výpisů, odeslané připomínky a další akce. Hodí se pro kontrolu, co aplikace udělala a kdy.",
        },
        {
          target: "[data-tour='dashboard-watchdog-details-link']",
          title: "Pojďme na stránku Hlídací pes",
          text: "Klikněte na „Zobrazit všechny hlídací psy“. Na samostatné stránce projdeme celý formulář, detailní nastavení a práci s výsledky.",
          href: "/hlidaci-pes",
        },
      ]
    : [
        {
          target: "[data-tour='dashboard-profile']",
          title: "Start with profile",
          text: "Open the profile first and fill in your name and billing details. The app can then use them for automatic invoice generation and correct email details.",
        },
        {
          target: "[data-tour='dashboard-profile-billing']",
          title: "What to fill in",
          text: "Name is used in communication. Billing details are used for invoices: business name, company ID, and address. Without them, invoices cannot be generated correctly.",
        },
        {
          target: "[data-tour='dashboard-watchdog']",
          title: "Watchdog overview",
          text: "This is where you track saved property searches. Watchdog helps monitor new listings by location, offer type, and other filters.",
        },
        {
          target: "[data-tour='watchdog-offer-type-field']",
          title: "Create a watchdog",
          text: "Choose whether you want to track sales or rentals. Otherwise, very different listing types would get mixed together.",
        },
        {
          target: "[data-tour='watchdog-detailed-search-button']",
          title: "Detailed filters",
          text: "Detailed search is for price, layout, size, and other limits. It helps reduce irrelevant listings.",
        },
        {
          target: "[data-tour='watchdog-create-form']",
          title: "What appears next",
          text: "After saving, found listings start appearing here. Save interesting ones, hide weak matches, and compare results by price.",
        },
        {
          target: "[data-tour='dashboard-portfolio']",
          title: "Portfolio overview",
          text: "Portfolio is for properties you own, manage, or use. It keeps finances, contracts, reminders, and important contacts in one clear place.",
        },
        {
          target: "[data-tour='portfolio-name-field']",
          title: "Add a property",
          text: "Name is your internal property label. It helps you quickly recognize which unit you are working with.",
        },
        {
          target: "[data-tour='portfolio-details-button']",
          title: "Add details",
          text: "The “Add details” button opens purchase price, current value, address, and other details. They make yield, cash flow, and analytics useful.",
        },
        {
          target: "[data-tour='portfolio-tenant-button']",
          title: "Tenant and manager",
          text: "Tenant helps track payments and communication. This contact is also useful for reminders and payment overview.",
        },
        {
          target: "[data-tour='dashboard-activity']",
          title: "Activity",
          text: "Here you see bank imports, sent reminders, and other actions. It helps verify what the app did and when.",
        },
        {
          target: "[data-tour='dashboard-watchdog-details-link']",
          title: "Let’s go to the Watchdog page",
          text: "Click “View all watchdogs”. On the dedicated page, we’ll cover the full form, detailed settings, and how to work with results.",
          href: "/hlidaci-pes",
        },
      ];
}

function buildWatchdogSteps(
  language: "cs" | "en",
  hasWatchdog: boolean,
  hasWatchdogResults: boolean,
  continueToPortfolio: boolean,
): GuidedTourStep[] {
  if (language === "cs") {
    const steps: GuidedTourStep[] = [
      {
        target: "[data-tour='watchdog-selection']",
        title: hasWatchdog ? "Vaše hledání" : "Vytvořte první hledání",
        text: hasWatchdog
          ? "Tady spravujete uložené hlídací psy. Každý může mít vlastní lokalitu, typ nabídky, cenu i další filtry."
          : "Nového hlídacího psa vytvoříte ve formuláři „Vytvořit nového hlídacího psa“. Vyplňte hlavní pole a pak hledání uložte.",
      },
    ];

    if (hasWatchdog) {
      steps.push({
        target: "[data-tour='watchdog-selected']",
        title: "Vyberte konkrétního psa",
        text: "Kliknutím přepnete, které hledání právě sledujete. Hodí se mít zvlášť třeba investici, bydlení nebo garáž.",
      });
    } else {
      steps.push(
        {
          target: "[data-tour='watchdog-offer-type-field']",
          title: "Typ nabídky",
          text: "Tady zvolíte, jestli chcete sledovat prodej nebo pronájem. Bez toho by se vám míchaly odlišné typy inzerátů.",
        },
        {
          target: "[data-tour='watchdog-estate-type-field']",
          title: "Typ nemovitosti",
          text: "Tady zúžíte výsledky třeba na byt, dům, pozemek nebo garáž. Díky tomu hlídací pes sleduje jen relevantní nabídky.",
        },
        {
          target: "[data-tour='watchdog-city-field']",
          title: "Vyplňte město a detaily",
          text: "Čím víc údajů vyplníte, tím přesnější budou výsledky. Město je základ lokality a hledání v okolí rozšíří záběr.",
        },
        {
          target: "[data-tour='watchdog-radius-field']",
          title: "Hledat v okolí",
          text: "Tahle volba rozšíří hledání za hranici zadaného města. Hodí se, když sledujete širší oblast kolem Olomouce, Prahy nebo menší obce.",
        },
        {
          target: "[data-tour='watchdog-detailed-search-button']",
          title: "Podrobnější hledání",
          text: "Podrobnější hledání otevře další filtry: cenu, dispozici, plochu nebo jiné parametry. Čím přesnější zadání, tím méně nerelevantních inzerátů.",
        },
        {
          target: "[data-tour='watchdog-create-form']",
          title: "Co se stane potom",
          text: "Po uložení se začnou zobrazovat výsledky. Nabídky pak můžete otevírat, ukládat mezi oblíbené nebo skrývat nerelevantní.",
        },
      );
    }

    if (hasWatchdogResults) {
      steps.push(
        {
          target: "[data-tour='watchdog-results']",
          title: "Projděte výsledky",
          text: "Výsledky můžete číst jako seznam, kompaktní karty nebo graf cen. Přepínače pohledu jsou užitečné podle toho, jestli řešíte detail, nebo trh.",
        },
        {
          target: "[data-tour='watchdog-results']",
          title: "Třiďte nabídky",
          text: "Zajímavé nabídky si uložte mezi oblíbené. Ty slabé skryjte, ať se vám v dalším porovnávání nepletou.",
        },
        {
          target: "[data-tour='watchdog-results']",
          title: "Sledujte cenu",
          text: "Graf pomůže rychle poznat, jestli nabídka vybočuje cenou, nebo jestli se v lokalitě hýbe celý trh.",
        },
      );
    }

    if (continueToPortfolio) {
      steps.push({
        target: "[data-tour='watchdog-selection']",
        title: "Pojďme do přehledu portfolia",
        text: "Pokračujte do portfolia, kde projdeme správu nemovitostí, finance, dokumenty a připomenutí.",
        href: "/portfolio",
      });
    }

    return steps;
  }

  const steps: GuidedTourStep[] = [
    {
      target: "[data-tour='watchdog-selection']",
      title: hasWatchdog ? "Your searches" : "Create your first search",
      text: hasWatchdog
        ? "Manage saved watchdogs here. Each one can have its own location, offer type, price range, and filters."
        : "Create a new watchdog in the “Create new watchdog” form. Fill in the main fields and save the search.",
    },
  ];

  if (hasWatchdog) {
    steps.push({
      target: "[data-tour='watchdog-selected']",
      title: "Select a watchdog",
      text: "Switch between searches here. It is useful to keep investment, living, or garage searches separate.",
    });
  } else {
    steps.push(
      {
        target: "[data-tour='watchdog-offer-type-field']",
        title: "Offer type",
        text: "Choose whether you want to track sale or rent here. Otherwise different listing types would be mixed together.",
      },
      {
        target: "[data-tour='watchdog-estate-type-field']",
        title: "Property type",
        text: "Narrow results to flats, houses, land, garages, and similar categories. This keeps the watchdog focused on relevant listings.",
      },
      {
        target: "[data-tour='watchdog-city-field']",
        title: "Fill in city and details",
        text: "The more details you fill in, the better the results. City is the base location and nearby search expands the area.",
      },
      {
        target: "[data-tour='watchdog-radius-field']",
        title: "Search nearby",
        text: "This expands the search outside the selected city. It is useful when watching a wider area around a city or smaller municipality.",
      },
      {
        target: "[data-tour='watchdog-detailed-search-button']",
        title: "Detailed search",
        text: "Detailed search opens filters such as price, layout, size, and other parameters. Better filters mean fewer irrelevant listings.",
      },
      {
        target: "[data-tour='watchdog-create-form']",
        title: "What happens next",
        text: "After saving, results start appearing below. You can open listings, save favorites, or hide irrelevant ones.",
      },
    );
  }

  if (hasWatchdogResults) {
    steps.push(
      {
        target: "[data-tour='watchdog-results']",
        title: "Review results",
        text: "Use list, compact cards, or the price chart depending on whether you need detail or market context.",
      },
      {
        target: "[data-tour='watchdog-results']",
        title: "Sort listings",
        text: "Save interesting listings as favorites. Hide weak matches so they do not clutter later comparisons.",
      },
      {
        target: "[data-tour='watchdog-results']",
        title: "Watch the price",
        text: "The chart helps you spot listings that are expensive, cheap, or moving with the wider local market.",
      },
    );
  }

  if (continueToPortfolio) {
    steps.push({
      target: "[data-tour='watchdog-selection']",
      title: "Let’s go to the portfolio overview",
      text: "Continue to the portfolio to review property management, finances, documents, and reminders.",
      href: "/portfolio",
    });
  }

  return steps;
}

function buildPortfolioSteps(
  language: "cs" | "en",
  hasPortfolioEstate: boolean,
): GuidedTourStep[] {
  if (language === "cs") {
    const steps: GuidedTourStep[] = [
      {
        target: "[data-tour='portfolio-selection']",
        title: hasPortfolioEstate
          ? "Vaše portfolio"
          : "Přidejte první nemovitost",
        text: hasPortfolioEstate
          ? "Tady přepínáte mezi celým portfoliem a konkrétní nemovitostí. Detail níže se vždy přizpůsobí výběru."
          : "První nemovitost přidáte ve formuláři „Přidat nemovitost“. Začněte názvem, typem nemovitosti a městem.",
      },
    ];

    if (hasPortfolioEstate) {
      steps.push(
        {
          target: "[data-tour='portfolio-detail']",
          title: "Doplňte provoz nemovitosti",
          text: "U vybrané nemovitosti spravujete nájem, zálohy, pravidelné výdaje a další transakce.",
        },
        {
          target: "[data-tour='portfolio-detail']",
          title: "Potvrzujte příjmy",
          text: "Příchozí platby si můžete potvrdit ručně nebo přes import banky. Výdaje se započítají automaticky.",
        },
        {
          target: "[data-tour='portfolio-documents']",
          title: "Hlídejte dokumenty",
          text: "Ke stejné nemovitosti ukládejte smlouvy, revize, výročí a připomenutí, aby nezůstaly mimo evidenci.",
        },
      );
    } else {
      steps.push(
        {
          target: "[data-tour='portfolio-name-field']",
          title: "Název nemovitosti",
          text: "Název je vaše vlastní pojmenování, třeba „Byt Olomouc“ nebo „Garáž“. Slouží hlavně k rychlé orientaci v portfoliu.",
        },
        {
          target: "[data-tour='portfolio-estate-type-field']",
          title: "Typ nemovitosti",
          text: "Typ nemovitosti pomůže portfolio třídit a zobrazovat ve správném kontextu, například byt, dům nebo garáž.",
        },
        {
          target: "[data-tour='portfolio-city-field']",
          title: "Vyplňte město a detaily",
          text: "Čím víc údajů vyplníte, tím lepší bude evidence i analytika. Město pomůže nemovitost odlišit od ostatních.",
        },
        {
          target: "[data-tour='portfolio-details-button']",
          title: "Detaily",
          text: "Tlačítko „Detaily“ otevře věci jako pořizovací cenu, aktuální hodnotu, adresu a další detaily. Bez těchto údajů nebude analytika ani výnos moc vypovídající.",
        },
        {
          target: "[data-tour='portfolio-tenant-button']",
          title: "Nájemce",
          text: "V části „Nájemce“ si uložíte kontakt na člověka, který nemovitost užívá. Hodí se pro přehled plateb, komunikaci a budoucí připomínky.",
        },
        {
          target: "[data-tour='portfolio-manager-button']",
          title: "Správce",
          text: "„Správce“ je člověk nebo firma, která řeší provoz nemovitosti. Díky tomu máte kontakty, poznámky a odpovědnosti u jedné nemovitosti.",
        },
        {
          target: "[data-tour='portfolio-create-form']",
          title: "Co přijde potom",
          text: "Po uložení se otevře detail nemovitosti. Tam přidáte transakce, dokumenty, výročí a připomenutí.",
        },
      );
    }

    return steps;
  }

  const steps: GuidedTourStep[] = [
    {
      target: "[data-tour='portfolio-selection']",
      title: hasPortfolioEstate ? "Your portfolio" : "Add your first property",
      text: hasPortfolioEstate
        ? "Switch between the whole portfolio and a specific property here. The detail below follows your selection."
        : "Add the first property in the “Add property” form. Start with name, property type, and city.",
    },
  ];

  if (hasPortfolioEstate) {
    steps.push(
      {
        target: "[data-tour='portfolio-detail']",
        title: "Add property operations",
        text: "For the selected property, manage rent, deposits, recurring expenses, and other transactions.",
      },
      {
        target: "[data-tour='portfolio-detail']",
        title: "Confirm income",
        text: "Incoming payments can be confirmed manually or through bank import. Expenses are included automatically.",
      },
      {
        target: "[data-tour='portfolio-documents']",
        title: "Track documents",
        text: "Keep contracts, inspections, anniversaries, and reminders attached to the same property.",
      },
    );
  } else {
    steps.push(
      {
        target: "[data-tour='portfolio-name-field']",
        title: "Property name",
        text: "Name is your own label, for example “Flat Olomouc” or “Garage”. It helps you quickly recognize the property.",
      },
      {
        target: "[data-tour='portfolio-estate-type-field']",
        title: "Property type",
        text: "Property type helps categorize the portfolio and display it in the right context, such as flat, house, or garage.",
      },
      {
        target: "[data-tour='portfolio-city-field']",
        title: "Fill in city and details",
        text: "The more details you fill in, the better the records and analytics. City helps separate the property from others.",
      },
      {
        target: "[data-tour='portfolio-details-button']",
        title: "Additional details",
        text: "The “Add details” button opens purchase price, current value, address, and other details. Without them, analytics and yield are much less useful.",
      },
      {
        target: "[data-tour='portfolio-tenant-button']",
        title: "Tenant",
        text: "The tenant section stores the person using the property. It helps with payment overview, communication, and later reminders.",
      },
      {
        target: "[data-tour='portfolio-manager-button']",
        title: "Manager",
        text: "Manager is the person or company handling property operations. It keeps contacts, notes, and responsibilities attached to the property.",
      },
      {
        target: "[data-tour='portfolio-create-form']",
        title: "What comes next",
        text: "After saving, the property detail opens. There you can add transactions, documents, anniversaries, and reminders.",
      },
    );
  }

  return steps;
}

export function PageTourButton({
  hiddenTargets = [],
  className,
  hasPortfolioEstate = false,
  hasWatchdog = false,
  hasWatchdogResults = false,
}: PageTourButtonProps) {
  const { language } = useLanguage();
  const pathname = usePathname();
  const [tourHandoff, setTourHandoff] = useState<TourHandoff | null>(null);
  const label = language === "cs" ? "Průvodce" : "Guide";
  const isWatchdogHandoff =
    pathname === "/hlidaci-pes" && tourHandoff === "watchdog";
  const isPortfolioHandoff =
    pathname === "/portfolio" && tourHandoff === "portfolio";

  useEffect(() => {
    const pendingHandoff = window.sessionStorage.getItem(
      TOUR_HANDOFF_STORAGE_KEY,
    );
    const expectedHandoff =
      pathname === "/hlidaci-pes"
        ? "watchdog"
        : pathname === "/portfolio"
          ? "portfolio"
          : null;

    if (pendingHandoff === expectedHandoff) {
      setTourHandoff(expectedHandoff);
      window.sessionStorage.removeItem(TOUR_HANDOFF_STORAGE_KEY);
    } else {
      setTourHandoff(null);
    }
  }, [pathname]);

  const steps =
    pathname === "/"
      ? buildDashboardSteps(language)
      : pathname === "/hlidaci-pes"
        ? buildWatchdogSteps(
            language,
            hasWatchdog,
            hasWatchdogResults,
            isWatchdogHandoff,
          )
        : pathname === "/portfolio"
          ? buildPortfolioSteps(language, hasPortfolioEstate)
          : [];
  const visibleSteps = steps.filter(
    (step) => !hiddenTargets.some((target) => step.target.includes(target)),
  );

  if (visibleSteps.length === 0) return null;

  return (
    <GuidedTour
      label={label}
      steps={visibleSteps}
      autoStart={isWatchdogHandoff || isPortfolioHandoff}
      onNavigate={(href) => {
        if (pathname === "/" && href === "/hlidaci-pes") {
          window.sessionStorage.setItem(TOUR_HANDOFF_STORAGE_KEY, "watchdog");
        } else if (isWatchdogHandoff && href === "/portfolio") {
          window.sessionStorage.setItem(TOUR_HANDOFF_STORAGE_KEY, "portfolio");
        }
      }}
      className={cn(className ?? "absolute right-4 top-4 z-30 shadow-sm")}
    />
  );
}
